<template>
  <Dropdown
    v-if="hasUserMenu"
    @menu-closed="handleUserMenuClosed"
    :placement="dropdownPlacement"
  >
    <Button
      class="block shrink-0"
      :class="{
        'panel-header-user': iconOnly,
        'panel-header-userchip__button': chip,
      }"
      variant="ghost"
      padding="tight"
      :trailing-icon="iconOnly || chip ? null : 'chevron-down'"
    >
      <span class="inline-flex items-center shrink-0 gap-2">
        <Icon
          type="finger-print"
          :solid="true"
          v-if="currentUser.impersonating"
          class="w-8 h-8"
        />
        <img
          v-else
          :alt="__(':name\'s Avatar', { name: userName })"
          :src="currentUser.avatar || '/images/user.png'"
          class="rounded-full w-8 h-8 object-cover"
        />

        <span v-if="!iconOnly" class="panel-header-userchip__name whitespace-nowrap">
          {{ userName }}
        </span>
      </span>
    </Button>

    <template #menu>
      <DropdownMenu width="200" class="px-1">
        <nav class="py-1">
          <component
            :is="item.component"
            v-for="item in formattedItems"
            :key="item.path"
            v-bind="item.props"
            v-on="item.on"
          >
            <span v-if="item.badge" class="mr-1">
              <Badge :extra-classes="item.badge.typeClass">
                {{ item.badge.value }}
              </Badge>
            </span>

            {{ item.name }}
          </component>

          <DropdownMenuItem
            as="button"
            v-if="currentUser.impersonating"
            @click="handleStopImpersonating"
          >
            {{ __('Stop Impersonating') }}
          </DropdownMenuItem>

          <DropdownMenuItem
            as="button"
            v-if="supportsAuthentication"
            @click="attempt"
          >
            {{ __('Logout') }}
          </DropdownMenuItem>
        </nav>
      </DropdownMenu>
    </template>
  </Dropdown>
  <div v-else-if="currentUser" class="flex items-center">
    <img
      :alt="__(':name\'s Avatar', { name: userName })"
      :src="currentUser.avatar || '/images/user.png'"
      class="rounded-full w-8 h-8 object-cover"
      :class="{ 'mr-3': !iconOnly }"
    />

    <span v-if="!iconOnly" class="whitespace-nowrap">
      {{ userName }}
    </span>
  </div>
</template>

<script>
import identity from 'lodash/identity'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import pickBy from 'lodash/pickBy'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { Button } from 'laravel-nova-ui'

export default {
  components: {
    Button,
  },

  props: {
    mobile: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },
    chip: { type: Boolean, default: false },
  },

  methods: {
    ...mapActions(['stopImpersonating']),
    ...mapMutations(['toggleMainMenu']),

    async attempt() {
      Nova.$emit('confirm-logout')
    },

    handleStopImpersonating() {
      if (confirm(this.__('Are you sure you want to stop impersonating?'))) {
        this.stopImpersonating()
      }
    },

    handleUserMenuClosed() {
      if (this.mobile === true) {
        this.toggleMainMenu()
      }
    },
  },

  computed: {
    ...mapGetters(['currentUser', 'userMenu']),

    fullName() {
      if(this.currentUser.last_name){
          if(this.currentUser.first_name){
            return `${this.currentUser.first_name} ${this.currentUser.last_name}`
          }
      } else {
        return this.currentUser.username
      }
    },

    userName() {
      return (
        this.fullName || this.__('Admin User')
      )
    },

    formattedItems() {
      return this.userMenu.map(i => {
        let method = i.method || 'GET'
        let props = { href: i.path }

        if (i.external && method == 'GET') {
          return {
            component: 'DropdownMenuItem',
            props: {
              ...props,
              target: i.target || null,
            },
            name: i.name,
            external: i.external,
            on: {},
          }
        }

        return {
          component: 'DropdownMenuItem',
          props: pickBy(
            omitBy(
              {
                ...props,
                method: method !== 'GET' ? method : null,
                data: i.data || null,
                headers: i.headers || null,
                as: method === 'GET' ? 'link' : 'form-button',
              },
              isNull
            ),
            identity
          ),
          external: i.external,
          name: i.name,
          on: {},
          badge: i.badge,
        }
      })
    },

    hasUserMenu() {
      return (
        this.currentUser &&
        (this.formattedItems.length > 0 ||
          this.supportsAuthentication ||
          this.currentUser.impersonating)
      )
    },

    supportsAuthentication() {
      return (
        Nova.config('withAuthentication') === true ||
        this.customLogoutPath !== false
      )
    },

    customLogoutPath() {
      return Nova.config('customLogoutPath')
    },

    componentName() {
      return 'Dropdown'
    },

    dropdownPlacement() {
      return this.mobile === true ? 'top-start' : 'bottom-end'
    },
  },
}
</script>
